import { useEffect, useState } from 'react'

import { formatDate } from '@/lib/date'

import { useMaintenanceStore } from '@/stores/useMaintenanceStore'

export default function MaintenancePage() {
  const { message, retryAfter } = useMaintenanceStore()
  const [countdown, setCountdown] = useState<string>('')

  useEffect(() => {
    if (!retryAfter) return

    const retryDate = new Date(retryAfter * 1000)

    const interval = setInterval(() => {
      const now = Date.now()
      const diff = retryDate.getTime() - now

      if (diff <= 0) {
        setCountdown('Checking availability...')
        window.location.reload()
        return
      }

      const minutes = Math.floor(diff / 60000)
      const seconds = Math.floor((diff % 60000) / 1000)
      setCountdown(`Estimated return: ${minutes}m ${seconds}s`)
    }, 1000)

    return () => clearInterval(interval)
  }, [retryAfter])

  return (
    <div className='fixed inset-0 flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4'>
      <div className='animate-in fade-in zoom-in w-full max-w-md duration-500'>
        <div className='rounded-2xl bg-white p-8 shadow-2xl'>
          {/* Icon */}
          <div className='mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 shadow-lg'>
            <svg className='h-10 w-10 animate-pulse text-white' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
              />
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
            </svg>
          </div>

          {/* Content */}
          <div className='text-center'>
            <h1 className='mb-3 text-2xl font-bold text-gray-900'>Scheduled Maintenance</h1>
            <p className='mb-4 text-gray-600'>
              {message || "We're currently performing scheduled maintenance to improve your experience."}
            </p>

            {/* Countdown or Retry info */}
            {retryAfter ? (
              <div className='mb-6 rounded-lg bg-purple-50 p-4'>
                <p className='text-sm font-medium text-purple-900'>{countdown}</p>
                <p className='mt-1 text-xs text-purple-700'>
                  Expected back: {formatDate(new Date(retryAfter * 1000), { style: 'dateTime' })}
                </p>
              </div>
            ) : (
              <div className='mb-6'>
                <div className='mx-auto h-1 w-48 overflow-hidden rounded-full bg-gray-200'>
                  <div className='animate-progress-gradient h-full w-full bg-gradient-to-r from-purple-500 via-indigo-500 to-purple-500 bg-[length:200%_100%]' />
                </div>
                <p className='mt-3 text-sm text-gray-500'>Please check back in a few minutes</p>
              </div>
            )}

            {/* Auto-refresh notice */}
            <p className='text-xs text-gray-400'>This page will automatically refresh when service is restored</p>
          </div>
        </div>

        {/* Support info */}
        <div className='mt-6 text-center'>
          <p className='text-sm text-gray-400'>
            Need immediate assistance?{' '}
            <a
              href={`mailto:${import.meta.env.VITE_APP_SUPPORT_EMAIL || 'contact@barbellagym.com'}`}
              className='font-medium underline hover:text-white'
            >
              Contact Support
            </a>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes progress-gradient {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .animate-progress-gradient {
          animation: progress-gradient 2s linear infinite;
        }
      `}</style>
    </div>
  )
}
